import { useState, useRef, useCallback, useEffect } from "react";
import Alert from "../../../../../components/alert";
import {
  Gamepad2,
  Calendar,
  MapPin,
  Crown,
  Trophy,
  Loader2,
  MessagesSquare,
  Play,
  Shield,
  Swords,
} from "lucide-react";
import "../styles/double-elimination-bracket.css";
import CustomAlert from "../../../../../components/custom-alert";
import { useAlert } from "../../../../../hooks/useAlert";
import { Round, Tournament } from "../../../../../common/services/tournament.types";
import { useAuth } from "../../../../../common/store/authStore";
import {
  updateDoubleEliminationScore,
  progressDoubleEliminationWinnersBracket,
  progressDoubleEliminationLosersBracket,
  completeDoubleEliminationFinal,
  shuffleTournamentBracket,
} from "../../../../../common/services/bracket";
import { startTournament } from "../../../../../common/services/tournament";
import {
  DoubleEliminationSection,
  PopulatedMatch,
} from "../../../../../common/services/bracket.types";
import LiveChat from "./live-chat";

interface DoubleEliminationBracketProps {
  tournament: Tournament;
}

const DoubleEliminationBracket = ({ tournament }: DoubleEliminationBracketProps) => {
  const [updatingRound, setUpdatingRound] = useState<string | null>(null);
  const [showShuffleAlert, setShowShuffleAlert] = useState<boolean>(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isCompletingFinal, setIsCompletingFinal] = useState(false);
  const [localScores, setLocalScores] = useState<
    Record<string, { score1: number | null; score2: number | null }>
  >({});
  const { alert, showSuccess, showError } = useAlert();
  const [showChat, setShowChat] = useState(false);
  const { authenticatedUser } = useAuth();
  const [updatingScores, setUpdatingScores] = useState<Set<string>>(new Set());
  const debounceTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const isHost = tournament?.host._id === authenticatedUser?.id;
  const bracket = tournament?.bracket;
  const winner = bracket?.tournamentWinner;
  const isCompleted = tournament.status === "completed";

  const winnersRoundKeys = bracket?.winners
    ? Object.keys(bracket.winners).sort()
    : [];
  const losersRoundKeys = bracket?.losers
    ? Object.keys(bracket.losers).sort()
    : [];
  const finalRound = bracket?.grandFinal?.final ? "final" : null;
  const winnersChampionWaiting =
    !!bracket?.winnersBracketWinner && !finalRound && !isCompleted;

  useEffect(() => {
    const timeouts = debounceTimeouts.current;

    return () => {
      timeouts.forEach((timeout) => {
        clearTimeout(timeout);
      });
      timeouts.clear();
    };
  }, []);

  const handleShuffle = async () => {
    try {
      setIsShuffling(true);
      await shuffleTournamentBracket(tournament._id);
      setShowShuffleAlert(false);
      window.location.reload();
    } catch (error: any) {
      showError("Shuffle Failed", error.message);
    } finally {
      setIsShuffling(false);
    }
  };

  const handleStartTournament = async () => {
    try {
      setIsStarting(true);
      await startTournament(tournament._id);
      showSuccess("Tournament Started", "Tournament is now live!");
      window.location.reload();
    } catch (error: any) {
      showError("Start Failed", error.message);
    } finally {
      setIsStarting(false);
    }
  };

  const handleScoreChange = useCallback(
    async (
      section: DoubleEliminationSection,
      roundName: string,
      matchId: string,
      participantId: string,
      score: string,
      isParticipant1: boolean,
    ) => {
      if (!tournament?._id) return;

      const scoreValue = parseInt(score) || 0;
      const uniqueKey = `${section}_${roundName}_${matchId}`;
      const scoreKey = `${uniqueKey}_${isParticipant1 ? "score1" : "score2"}`;

      const existingTimeout = debounceTimeouts.current.get(scoreKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      setLocalScores((prev) => ({
        ...prev,
        [uniqueKey]: {
          ...prev[uniqueKey],
          [isParticipant1 ? "score1" : "score2"]: scoreValue,
        },
      }));

      const timeout = setTimeout(async () => {
        debounceTimeouts.current.delete(scoreKey);
        setUpdatingScores((prev) => new Set(prev).add(scoreKey));

        try {
          const { match: updatedMatch } = await updateDoubleEliminationScore(
            tournament._id,
            {
              section,
              roundName,
              matchId,
              participantId,
              score: scoreValue,
            },
          );

          setLocalScores((prev) => ({
            ...prev,
            [uniqueKey]: {
              score1: updatedMatch.score1,
              score2: updatedMatch.score2,
            },
          }));
        } catch (error: any) {
          setLocalScores((prev) => {
            const newState = { ...prev };
            delete newState[uniqueKey];
            return newState;
          });
          showError("Score Update Failed", error.message);
        } finally {
          setUpdatingScores((prev) => {
            const newSet = new Set(prev);
            newSet.delete(scoreKey);
            return newSet;
          });
        }
      }, 3000);

      debounceTimeouts.current.set(scoreKey, timeout);
    },
    [tournament?._id, showError],
  );

  const handleProgressWinners = async (roundName: string) => {
    if (!tournament?._id) return;

    try {
      setUpdatingRound(`winners_${roundName}`);
      await progressDoubleEliminationWinnersBracket(tournament._id, { roundName });
      showSuccess("Round Updated", "Winners bracket round progressed successfully!");
      window.location.reload();
    } catch (error: any) {
      showError("Update Failed", error.message);
    } finally {
      setUpdatingRound(null);
    }
  };

  const handleProgressLosers = async (roundName: string) => {
    if (!tournament?._id) return;

    try {
      setUpdatingRound(`losers_${roundName}`);
      await progressDoubleEliminationLosersBracket(tournament._id, { roundName });
      showSuccess("Round Updated", "Losers bracket round progressed successfully!");
      window.location.reload();
    } catch (error: any) {
      showError("Update Failed", error.message);
    } finally {
      setUpdatingRound(null);
    }
  };

  const handleCompleteFinal = async () => {
    if (!tournament?._id) return;

    try {
      setIsCompletingFinal(true);
      const result = await completeDoubleEliminationFinal(tournament._id);
      showSuccess("Final Updated", result.message);
      window.location.reload();
    } catch (error: any) {
      showError("Update Failed", error.message);
    } finally {
      setIsCompletingFinal(false);
    }
  };

  const getMatchScore = (
    match: PopulatedMatch,
    scoreKey: "score1" | "score2",
    uniqueKey: string,
  ) => {
    return localScores[uniqueKey]?.[scoreKey] ?? match[scoreKey];
  };

  const getMatchStatus = (match: PopulatedMatch, uniqueKey: string) => {
    if (!match.participant2) return "completed";
    const score1 = getMatchScore(match, "score1", uniqueKey);
    const score2 = getMatchScore(match, "score2", uniqueKey);
    if (score1 !== null && score2 !== null) return "completed";
    if (score1 !== null || score2 !== null) return "in-progress";
    return "pending";
  };

  const getWinner = (match: PopulatedMatch, uniqueKey: string) => {
    if (!match.participant2) return match.participant1;
    const score1 = getMatchScore(match, "score1", uniqueKey);
    const score2 = getMatchScore(match, "score2", uniqueKey);
    if (score1 === null || score2 === null) return null;
    return score1 > score2 ? match.participant1 : match.participant2;
  };

  const getAvatarUrl = (imageUrl: string | null, username: string) => {
    return imageUrl || `https://robohash.org/${username}?set=set2&size=200x200`;
  };

  const areAllScoresComplete = (round: Round | undefined, sectionPrefix: string) => {
    if (!round) return false;
    const matches = Object.values(round) as PopulatedMatch[];
    if (matches.length === 0) return false;
    return matches.every((match) => {
      if (!match.participant2) return true;
      const uniqueKey = `${sectionPrefix}_${match.id}`;
      const score1 = getMatchScore(match, "score1", uniqueKey);
      const score2 = getMatchScore(match, "score2", uniqueKey);
      return score1 !== null && score2 !== null;
    });
  };

  const hasAnyScores = () => {
    const allRounds = [
      ...winnersRoundKeys.map((key) => bracket?.winners?.[key]),
      ...losersRoundKeys.map((key) => bracket?.losers?.[key]),
      bracket?.grandFinal?.final,
    ].filter(Boolean) as Round[];

    return allRounds.some((round) =>
      (Object.values(round) as PopulatedMatch[]).some(
        (match) => match.score1 !== null || match.score2 !== null,
      ),
    );
  };

  const lastLosersRoundKey = losersRoundKeys[losersRoundKeys.length - 1];
  const lastLosersRound = lastLosersRoundKey ? bracket?.losers?.[lastLosersRoundKey] : undefined;
  const lastLosersRoundAlreadyProgressed =
    !!lastLosersRoundKey &&
    lastLosersRoundKey === bracket?.doubleEliminationState?.lastProgressedLosersRound;
  const losersPendingProgress =
    !!lastLosersRound &&
    areAllScoresComplete(lastLosersRound, `losers_${lastLosersRoundKey}`) &&
    !lastLosersRoundAlreadyProgressed &&
    !bracket?.losersBracketWinner;

  const renderMatchCard = (
    match: PopulatedMatch,
    matchIndex: number,
    section: DoubleEliminationSection,
    roundKey: string,
    editable: boolean,
  ) => {
    const uniqueKey = `${section}_${roundKey}_${match.id}`;
    const status = getMatchStatus(match, uniqueKey);
    const matchWinner = getWinner(match, uniqueKey);
    const isByeRound = !match.participant2;

    return (
      <div key={match.id} className={`match-card ${status}`}>
        <div className="match-header">
          <span className="match-number">Match {matchIndex + 1}</span>
          {matchWinner && (
            <div className="match-winner">
              <Trophy />
              <span>{matchWinner.username}</span>
            </div>
          )}
        </div>

        <div className="players-container">
          {isByeRound ? (
            <div className="bye-card">
              <span>Bye Round</span>
              <span className="bye-player">
                {match.participant1?.username} advances
              </span>
            </div>
          ) : (
            <>
              {(["participant1", "participant2"] as const).map((participantKey, index) => {
                const participant = match[participantKey];
                const scoreKey = index === 0 ? "score1" : "score2";
                const isWinner = matchWinner?.id === participant?.id;

                return (
                  <div key={participantKey}>
                    <div className={`player-card ${isWinner ? "winner" : ""}`}>
                      <div className="player-avatar">
                        <img
                          src={getAvatarUrl(participant?.imageUrl, participant?.username)}
                          alt={participant?.username}
                        />
                      </div>
                      <div className="player-info">
                        <span className="player-name">{participant?.username}</span>
                        {isWinner && <span className="player-status">Winner</span>}
                      </div>
                      <div className="player-score">
                        {isHost && editable ? (
                          <div className="score-input-container">
                            <input
                              type="number"
                              value={getMatchScore(match, scoreKey, uniqueKey) ?? ""}
                              onChange={(e) =>
                                handleScoreChange(
                                  section,
                                  roundKey,
                                  match.id,
                                  participant.id,
                                  e.target.value,
                                  index === 0,
                                )
                              }
                              className={`score-input ${
                                debounceTimeouts.current.has(`${uniqueKey}_${scoreKey}`)
                                  ? "score-input-pending"
                                  : ""
                              }`}
                              placeholder="0"
                              min="0"
                              disabled={updatingScores.size > 0}
                            />
                            {updatingScores.has(`${uniqueKey}_${scoreKey}`) && (
                              <Loader2 className="score-loading-spinner" />
                            )}
                            {debounceTimeouts.current.has(`${uniqueKey}_${scoreKey}`) &&
                              !updatingScores.has(`${uniqueKey}_${scoreKey}`) && (
                                <div className="score-pending-indicator">
                                  <div className="pending-dot"></div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <span className="score-display">
                            {getMatchScore(match, scoreKey, uniqueKey) ?? "-"}
                          </span>
                        )}
                      </div>
                    </div>
                    {index === 0 && (
                      <div className="vs-divider">
                        <span>VS</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="match-status">
          <span className={`status-badge ${status}`}>
            {status === "pending" && "Pending"}
            {status === "in-progress" && "In Progress"}
            {status === "completed" && "Completed"}
          </span>
        </div>
      </div>
    );
  };

  const renderBracketSection = (
    section: "winners" | "losers",
    title: string,
    Icon: typeof Trophy,
    description: string,
  ) => {
    const roundKeys = section === "winners" ? winnersRoundKeys : losersRoundKeys;
    const sectionWinner =
      section === "winners" ? bracket?.winnersBracketWinner : bracket?.losersBracketWinner;

    if (roundKeys.length === 0) {
      return null;
    }

    return (
      <div className="bracket-section">
        <div className="bracket-section-header">
          <h3 className="bracket-section-title">
            <Icon className="section-icon" />
            {title}
          </h3>
          <p className="bracket-section-description">{description}</p>
        </div>

        {roundKeys.map((roundKey, roundIndex) => {
          const round = section === "winners" ? bracket?.winners?.[roundKey] : bracket?.losers?.[roundKey];
          if (!round) return null;

          const matches = Object.values(round) as PopulatedMatch[];
          const isLastRound = roundIndex === roundKeys.length - 1;
          const isFinalRound =
            isLastRound && matches.length === 1 && matches[0].participant1 && matches[0].participant2;
          const alreadyProgressed =
            section === "losers" &&
            roundKey === bracket?.doubleEliminationState?.lastProgressedLosersRound;
          const editable = isLastRound && !sectionWinner && !finalRound && !isCompleted && !alreadyProgressed;

          return (
            <div key={roundKey} className="bracket-round-section">
              <div className="round-header">
                <h3 className="round-title">
                  {isFinalRound ? `${title} Final` : `Round ${roundIndex + 1}`}
                </h3>
                <div className="round-stats">
                  <span>
                    {matches.length} {matches.length === 1 ? "Match" : "Matches"}
                  </span>
                </div>
              </div>

              <div className="matches-grid">
                {matches.map((match, matchIndex) =>
                  renderMatchCard(match, matchIndex, section, roundKey, editable),
                )}
              </div>

              {isHost && editable && areAllScoresComplete(round, `${section}_${roundKey}`) && (
                <div className="round-actions">
                  {section === "winners" && losersPendingProgress ? (
                    <p className="round-blocked-hint">
                      Progress the losers bracket round below before advancing the winners
                      bracket.
                    </p>
                  ) : (
                    <button
                      className="update-round-btn"
                      onClick={() =>
                        section === "winners"
                          ? handleProgressWinners(roundKey)
                          : handleProgressLosers(roundKey)
                      }
                      disabled={updatingRound === `${section}_${roundKey}`}
                    >
                      {updatingRound === `${section}_${roundKey}` ? "Updating..." : "Progress Round"}
                    </button>
                  )}
                </div>
              )}

              {isHost && section === "losers" && isLastRound && alreadyProgressed && !isCompleted && (
                <div className="round-actions">
                  <p className="round-blocked-hint">
                    Waiting for the winners bracket to drop the next opponent into this round.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="double-elimination-container">
      <div className="tournament-header">
        <div className="tournament-info">
          <h2 className="tournament-title">{tournament?.tournamentName}</h2>
          <div className="tournament-meta">
            <span className="meta-item">
              <Gamepad2 />
              {tournament?.selectedGame}
            </span>
            <span className="meta-item">
              <Calendar />
              {tournament?.convertedDateReadable || tournament?.tournamentDate}
            </span>
            <span className="meta-item">
              <MapPin />
              {tournament?.location}
            </span>
          </div>
        </div>
        {winner && (
          <div className="winner-banner">
            <Crown className="winner-icon" />
            <span>Winner: {winner.username}</span>
          </div>
        )}
        {!showChat && (
          <div className="live-chat-container">
            <p className="live-chat-text">Live chat</p>
            <div className="live-desc-container">
              <MessagesSquare />
              <span>
                Join the live chat to interact with the host, participants and others watching
                this live fixture.
              </span>
              <button className="join-chat-btn" onClick={() => setShowChat(true)}>
                Join Chat
              </button>
            </div>
          </div>
        )}
      </div>

      {showChat && (
        <LiveChat
          tournamentId={tournament._id}
          status={tournament.status}
          isHost={isHost}
          participants={tournament.participants || []}
          onClose={() => setShowChat(false)}
        />
      )}

      <div className="bracket-legend">
        <div className="legend-item">
          <div className="legend-color legend-pending"></div>
          <span>Pending</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-in-progress"></div>
          <span>In Progress</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-completed"></div>
          <span>Completed</span>
        </div>
        <div className="legend-item">
          <div className="legend-color legend-winner"></div>
          <span>Winner</span>
        </div>
      </div>

      {renderBracketSection(
        "winners",
        "Winners Bracket",
        Trophy,
        "Lose here and drop to the losers bracket.",
      )}

      {renderBracketSection(
        "losers",
        "Losers Bracket",
        Shield,
        "One more loss here and you're eliminated.",
      )}

      {winnersChampionWaiting && (
        <div className="round-blocked-hint" style={{ margin: "0 0 25px" }}>
          {bracket?.winnersBracketWinner?.username} won the Winners Bracket and is waiting in the
          Winners Final while the Losers Bracket plays out.
        </div>
      )}

      {finalRound && bracket && (
        <div className="bracket-section final-section">
          <div className="bracket-section-header">
            <h3 className="bracket-section-title">
              <Swords className="section-icon" />
              Final Match
            </h3>
            <p className="bracket-section-description">
              Winners bracket champion faces the losers bracket champion.
            </p>
          </div>

          <div className="final-match-container">
            <div className="matches-grid">
              {Object.values(bracket.grandFinal?.[finalRound] || {}).map((match, matchIndex) =>
                renderMatchCard(
                  match as PopulatedMatch,
                  matchIndex,
                  "grandFinal",
                  finalRound,
                  isHost && !isCompleted,
                ),
              )}
            </div>

            {isHost &&
              !isCompleted &&
              areAllScoresComplete(bracket.grandFinal?.[finalRound], `grandFinal_${finalRound}`) && (
                <div className="round-actions">
                  <button
                    className="update-round-btn"
                    onClick={handleCompleteFinal}
                    disabled={isCompletingFinal}
                  >
                    {isCompletingFinal ? "Updating..." : "Complete Final"}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      {isCompleted && winner && (
        <div className="match-card winner-final" style={{ marginTop: 30 }}>
          <div className="winner-final-display">
            <div className="winner-crown-large">
              <Crown />
            </div>
            <div className="winner-avatar-large">
              <img src={getAvatarUrl(winner.imageUrl, winner.username)} alt={winner.username} />
            </div>
            <div className="winner-info-large">
              <h2 className="champion-title">Winner</h2>
              <h3 className="champion-name">{winner.username}</h3>
            </div>
            <div className="winner-stats">
              <div className="stat-item">
                <span className="stat-label">Tournament</span>
                <span className="stat-value">{tournament?.tournamentName}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Game</span>
                <span className="stat-value">{tournament?.selectedGame}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Date</span>
                <span className="stat-value">{tournament?.convertedDateReadable}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {isHost && tournament.status === "upcoming" && (
        <div className="shuffle-section">
          <button
            className="shuffle-btn"
            onClick={handleStartTournament}
            disabled={isStarting}
          >
            {isStarting ? (
              <>
                <Loader2 className="spinner" />
                Starting...
              </>
            ) : (
              <>
                <Play />
                Start Tournament
              </>
            )}
          </button>
        </div>
      )}

      {isHost && tournament.status !== "upcoming" && winnersRoundKeys.length > 0 && !hasAnyScores() && (
        <div className="shuffle-section">
          <button
            className="shuffle-btn"
            onClick={() => setShowShuffleAlert(true)}
            disabled={isShuffling}
          >
            {isShuffling ? (
              <>
                <Loader2 className="spinner" />
                Shuffling...
              </>
            ) : (
              <>
                <Gamepad2 />
                Shuffle Participants
              </>
            )}
          </button>
        </div>
      )}

      <CustomAlert
        isOpen={showShuffleAlert}
        onClose={() => setShowShuffleAlert(false)}
        onConfirm={handleShuffle}
        title="Are you sure?"
        message="Shuffling will reset the current tournament brackets."
        confirmText="Yes, shuffle!"
        cancelText="Cancel"
        type="warning"
      />

      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        duration={4000}
        position="top-right"
      />
    </div>
  );
};

export default DoubleEliminationBracket;
